/**
 * Sponsor Discovery update - pure state transition logic.
 * 
 * Elm-ish architecture: This function is pure.
 * - No API calls
 * - No storage access
 * - No hooks
 * - No side effects
 * - No mutation
 * 
 * Each message produces a new model and any effects that need to run.
 */
import type { SponsorDiscoveryModel } from './model';
import type { SponsorDiscoveryMsg } from './messages';
import type { SponsorDiscoveryAction } from './model';

/**
 * Effect types that can be triggered by state transitions.
 * These are handled by the effect runner (useSponsorDiscoveryProgram.ts).
 */
export type SponsorDiscoveryEffect =
  | { readonly type: 'LoadCards' }
  | { readonly type: 'SkipCard'; readonly cardId: string }
  | { readonly type: 'SaveCard'; readonly cardId: string }
  | { readonly type: 'RequestIntroCard'; readonly cardId: string }
  | { readonly type: 'LoadMoreCards'; readonly offset: number; readonly limit: number };

/**
 * Result of an update call - new model plus effects to run.
 */
export interface UpdateResult {
  readonly model: SponsorDiscoveryModel;
  readonly effects: readonly SponsorDiscoveryEffect[];
}

/**
 * Pure update function - the heart of the Elm-ish architecture.
 * 
 * @param model - Current state
 * @param msg - Message triggering the transition
 * @returns New state and effects to run
 */
export function update(
  model: SponsorDiscoveryModel,
  msg: SponsorDiscoveryMsg
): UpdateResult {
  switch (msg.type) {
    case 'Started':
      return {
        model: {
          ...model,
          status: 'loading',
          error: null,
        },
        effects: [{ type: 'LoadCards' }],
      };

    case 'LoadSucceeded': {
      if (msg.cards.length === 0) {
        return {
          model: {
            ...model,
            status: 'empty',
            cards: [],
            activeIndex: 0,
            error: null,
            pendingAction: null,
            nextOffset: 0, // Zero cards -> zero offset for semantically cleaner state
            hasMore: false,
          },
          effects: [],
        };
      }
      const hasMore = msg.cards.length === model.pageSize;
      return {
        model: {
          ...model,
          status: 'ready',
          cards: msg.cards,
          activeIndex: 0,
          error: null,
          pendingAction: null,
          nextOffset: msg.cards.length,
          hasMore,
        },
        effects: [],
      };
    }

    case 'LoadFailed':
      return {
        model: {
          ...model,
          status: 'error',
          error: msg.error,
        },
        effects: [],
      };

    case 'SkipClicked': {
      const activeCard = model.cards[model.activeIndex];
      if (!activeCard) return { model, effects: [] };
      return {
        model: {
          ...model,
          pendingAction: 'skip',
        },
        effects: [{ type: 'SkipCard', cardId: activeCard.id }],
      };
    }

    case 'SaveClicked': {
      const activeCard = model.cards[model.activeIndex];
      if (!activeCard) return { model, effects: [] };
      return {
        model: {
          ...model,
          pendingAction: 'save',
        },
        effects: [{ type: 'SaveCard', cardId: activeCard.id }],
      };
    }

    case 'RequestIntroClicked': {
      const activeCard = model.cards[model.activeIndex];
      if (!activeCard) return { model, effects: [] };
      return {
        model: {
          ...model,
          pendingAction: 'requestIntro',
          // Track card ID for success path
          optimisticIntroCardId: activeCard.id,
        },
        effects: [{ type: 'RequestIntroCard', cardId: activeCard.id }],
      };
    }

    case 'ActionSucceeded': {
      // If this was an intro request, add to requestedIntros on success
      let newRequestedIntros = model.requestedIntros;
      if (model.optimisticIntroCardId) {
        newRequestedIntros = new Set(model.requestedIntros);
        newRequestedIntros.add(model.optimisticIntroCardId);
      }
      const result = advanceCard(model, 'skip');
      return {
        model: {
          ...result.model,
          requestedIntros: newRequestedIntros,
          optimisticIntroCardId: null,
        },
        effects: result.effects,
      };
    }

    case 'ActionFailed':
      return {
        model: {
          ...model,
          pendingAction: null,
          error: msg.error, // Store error for user visibility
          optimisticIntroCardId: null, // Clear optimistic tracking on failure
        },
        effects: [],
      };

    case 'RetryClicked':
      return {
        model: {
          ...model,
          status: 'loading',
          error: null,
        },
        effects: [{ type: 'LoadCards' }],
      };

    case 'LoadMoreClicked':
      return {
        model: {
          ...model,
          status: 'loading',
        },
        effects: [{ type: 'LoadMoreCards', offset: model.nextOffset, limit: model.pageSize }],
      };

    case 'LoadMoreSucceeded': {
      if (msg.cards.length === 0) {
        return {
          model: {
            ...model,
            status: 'ready',
            hasMore: false,
          },
          effects: [],
        };
      }
      const hasMore = msg.cards.length === model.pageSize;
      return {
        model: {
          ...model,
          status: 'ready',
          cards: [...model.cards, ...msg.cards],
          nextOffset: model.nextOffset + msg.cards.length,
          hasMore,
        },
        effects: [],
      };
    }

    case 'LoadMoreFailed':
      return {
        model: {
          ...model,
          status: 'ready', // Return to ready state, keep existing cards
          error: msg.error,
        },
        effects: [],
      };

    default:
      // Exhaustive check - should never reach here
      const _exhaustive: never = msg;
      return { model, effects: [] };
  }
}

/**
 * Helper to advance to the next card or enter empty state.
 * Clears any pending action.
 */
function advanceCard(
  model: SponsorDiscoveryModel,
  _action: SponsorDiscoveryAction
): UpdateResult {
  const nextIndex = model.activeIndex + 1;
  
  // If there are more cards, advance to next
  if (nextIndex < model.cards.length) {
    return {
      model: {
        ...model,
        activeIndex: nextIndex,
        pendingAction: null,
      },
      effects: [],
    };
  }
  
  // Last card consumed - enter empty state
  return {
    model: {
      ...model,
      status: 'empty',
      activeIndex: 0,
      pendingAction: null,
    },
    effects: [],
  };
}